import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, BACKEND_URL, CATEGORY_NAME, fmtDuration } from "@/lib/api";
import { RiderProfileInner } from "./RiderProfile";
import { Heart, MessageSquare, Send, Eye } from "lucide-react";
import { toast } from "sonner";

const REACTIONS = [
  { id: "fire", emoji: "🔥" },
  { id: "clap", emoji: "👏" },
  { id: "heart", emoji: "❤️" },
  { id: "wow", emoji: "🤠" },
  { id: "rope", emoji: "🪢" },
];

export default function SharePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/share/${token}`)
      .then((r) => setData(r.data))
      .catch((err) => setError(err?.response?.data?.detail || "Share link invalid"));
  }, [token]);

  if (error) {
    return (
      <div className="p-12 text-center text-stone-500" data-testid="share-error">
        <div className="text-rose-400 text-lg mb-2">Link unavailable</div>
        <div className="text-sm">{error}</div>
      </div>
    );
  }
  if (!data) return <div className="p-12 text-center text-stone-500">Loading…</div>;

  if (data.type === "rider") {
    return (
      <div data-testid="share-rider-view">
        <ShareBanner subtitle="Public shared profile · Reactions and comments are open" />
        <RiderProfileInner data={data} shareMode />
      </div>
    );
  }
  if (data.type === "clip") {
    return <ClipShareView clip={data.clip} />;
  }
  return <div className="p-12 text-stone-500">Unsupported share type</div>;
}

function ShareBanner({ subtitle }) {
  return (
    <div className="bg-orange-600/10 border-b border-orange-700 px-6 py-2 text-center text-xs uppercase tracking-widest text-orange-400">
      {subtitle}
    </div>
  );
}

function ClipShareView({ clip }) {
  const [counts, setCounts] = useState({});
  const [comments, setComments] = useState([]);
  const [name, setName] = useState(() => localStorage.getItem("rcs_visitor_name") || "");
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const [viewed, setViewed] = useState(false);

  useEffect(() => {
    api.get(`/clips/${clip.id}/reactions`).then((r) => setCounts(r.data.counts || {}));
    api.get(`/clips/${clip.id}/comments`).then((r) => setComments(r.data.comments || []));
  }, [clip.id]);

  const handlePlay = () => {
    if (viewed) return;
    setViewed(true);
    api.post(`/clips/${clip.id}/view`).catch(() => {});
  };
  const react = async (emoji) => {
    try {
      const r = await api.post(`/clips/${clip.id}/reactions`, { emoji, by_name: name });
      setCounts(r.data.counts);
    } catch {
      toast.error("Failed");
    }
  };
  const post = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setPosting(true);
    try {
      const r = await api.post(`/clips/${clip.id}/comments`, { text, by_name: name || "Anonymous" });
      setComments((prev) => [r.data.comment, ...prev]);
      if (name) localStorage.setItem("rcs_visitor_name", name);
      setText("");
    } catch (err) {
      toast.error("Failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setPosting(false);
    }
  };

  return (
    <div data-testid="share-clip-view">
      <ShareBanner subtitle="Public shared clip · Reactions and comments are open" />
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="border-l-4 border-orange-600 pl-4 mb-4">
          <div className="label-tiny mb-1">{CATEGORY_NAME[clip.category]}</div>
          <h1 className="font-display uppercase text-3xl sm:text-4xl font-bold leading-tight">
            {clip.sub_label || "Run"}
          </h1>
          {clip.rider_horse_name && <div className="text-orange-400 mt-1">{clip.rider_horse_name}</div>}
        </div>
        <video
          src={clip.stream_url || `${BACKEND_URL}/api/clips/${clip.id}/stream`}
          controls
          autoPlay
          onPlay={handlePlay}
          className="w-full bg-black border border-stone-800"
        />
        <div className="font-mono-tc text-stone-500 text-xs mt-2 flex justify-between">
          <span><Eye size={11} className="inline mr-1" />{(counts.views || 0)} watching</span>
          <span>{fmtDuration(clip.duration)}</span>
        </div>
        {clip.notes && <div className="text-sm text-stone-300 italic mt-4 p-3 border-l-2 border-stone-800">{clip.notes}</div>}

        <div className="mt-6 border-t border-stone-800 pt-4">
          <div className="label-tiny mb-2 flex items-center gap-2"><Heart size={12} /> React</div>
          <div className="flex flex-wrap gap-2">
            {REACTIONS.map((r) => (
              <button key={r.id} onClick={() => react(r.id)} className="btn-secondary !py-2 !px-3 inline-flex items-center gap-2 hover:!border-orange-700" data-testid={`react-${r.id}`}>
                <span className="text-lg">{r.emoji}</span>
                <span className="font-mono-tc text-stone-300">{counts[r.id] || 0}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6 border-t border-stone-800 pt-4">
          <div className="label-tiny mb-3 flex items-center gap-2"><MessageSquare size={12} /> Comments ({comments.length})</div>
          <form onSubmit={post} className="space-y-2 mb-4">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name (optional)" className="field-input" data-testid="comment-name" />
            <div className="flex gap-2">
              <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Leave a note…" className="field-input flex-1" data-testid="comment-text" />
              <button type="submit" disabled={posting || !text.trim()} className="btn-primary inline-flex items-center gap-1.5"><Send size={12} /> Post</button>
            </div>
          </form>
          <div className="space-y-3">
            {comments.length === 0 && <div className="text-stone-500 text-sm italic">No comments yet — be first.</div>}
            {comments.map((c) => (
              <div key={c.id} className="border-l-2 border-stone-800 pl-3">
                <div className="text-xs text-stone-500 font-mono-tc mb-0.5">
                  <span className="text-orange-400">{c.by_name}</span> · {(c.created_at || "").slice(0, 16).replace("T", " ")}
                </div>
                <div className="text-sm text-stone-200 whitespace-pre-wrap">{c.text}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
